import { useMemo } from "react";
import { Asset } from "app/types";
import { btnDelay } from "common/utils";
import i18n from "features/shared/i18n";

export enum AssetTabType {
  ALBUM = "album",
  SKETCHBOOK = "sketchbook",
  AI_IMAGE = "ai_image",
}

function isAIAsset(asset: Asset): boolean {
  return asset.tags?.startsWith("ai_generated:") ?? false;
}

interface AssetsSectionProps {
  assets: Asset[];
  selectedAssetTabs: Set<AssetTabType>;
  onSelectAsset: (asset: Asset) => void;
  onShowMore: () => void;
  onToggleTab: (tab: AssetTabType) => void;
  showSketchbook?: boolean;
}

export function AssetsSection({
  assets,
  selectedAssetTabs,
  onSelectAsset,
  onShowMore,
  onToggleTab,
  showSketchbook = true,
}: AssetsSectionProps) {
  const filteredAssets = useMemo(() => {
    return assets
      .filter((asset) => {
        const isSvg = /.svg/.test(asset.url);
        const isAI = isAIAsset(asset);

        if (isAI && selectedAssetTabs.has(AssetTabType.AI_IMAGE)) return true;
        if (isSvg && !isAI && selectedAssetTabs.has(AssetTabType.SKETCHBOOK)) return true;
        if (!isSvg && !isAI && selectedAssetTabs.has(AssetTabType.ALBUM)) return true;

        return false;
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [assets, selectedAssetTabs]);

  const albumCount = useMemo(
    () => assets.filter((a) => !/.svg/.test(a.url) && !isAIAsset(a)).length,
    [assets],
  );
  const sketchbookCount = useMemo(
    () => assets.filter((a) => /.svg/.test(a.url) && !isAIAsset(a)).length,
    [assets],
  );
  const aiImageCount = useMemo(() => assets.filter(isAIAsset).length, [assets]);

  return (
    <div className="bg-white rounded-[16px] p-6">
      <div className="flex-row-el items-center justify-between mb-2">
        <div className="flex-row-el items-center gap-2 text-[24px] text-textcolor-black">
          <span className="material-symbols-outlined !text-[45px] text-red-100">palette</span>
          {i18n.t("MSG_PARENT_PAGE_PLAYER_ASSETS")}
        </div>
        {assets.length > 0 && (
          <button
            className="flex-row-el items-center gap-1 px-4 py-2 bg-white border border-gray2-60 rounded-full text-[21px] text-textcolor-black cursor-pointer hover:bg-gray2-20 transition-all"
            onClick={() => {
              btnDelay(onShowMore);
            }}
          >
            {i18n.t("MSG_PARENT_PAGE_PLAYER_SHOW_MORE")}
          </button>
        )}
      </div>

      <div className="flex-row-el gap-2 mb-4 pb-3 border-b border-outline">
        <button
          className={`px-4 py-2 rounded-[12px] text-[21px] border-none transition-all ${
            selectedAssetTabs.has(AssetTabType.ALBUM)
              ? "bg-ink text-white"
              : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
          }`}
          onClick={() => {
            onToggleTab(AssetTabType.ALBUM);
          }}
        >
          {i18n.t("MSG_PARENT_PAGE_PLAYER_ALBUM")}
          <span className="ml-1 text-[18px] opacity-80">({albumCount})</span>
        </button>
        {showSketchbook && (
          <button
            className={`px-4 py-2 rounded-[12px] text-[21px] border-none transition-all ${
              selectedAssetTabs.has(AssetTabType.SKETCHBOOK)
                ? "bg-ink text-white"
                : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
            }`}
            onClick={() => {
              onToggleTab(AssetTabType.SKETCHBOOK);
            }}
          >
            {i18n.t("MSG_PARENT_PAGE_PLAYER_SKETCHBOOK")}
            <span className="ml-1 text-[18px] opacity-80">({sketchbookCount})</span>
          </button>
        )}
        <button
          className={`px-4 py-2 rounded-[12px] text-[21px] border-none transition-all ${
            selectedAssetTabs.has(AssetTabType.AI_IMAGE)
              ? "bg-ink text-white"
              : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
          }`}
          onClick={() => {
            onToggleTab(AssetTabType.AI_IMAGE);
          }}
        >
          {i18n.t("MSG_PARENT_PAGE_PLAYER_AI_IMAGE")}
          <span className="ml-1 text-[18px] opacity-80">({aiImageCount})</span>
        </button>
      </div>

      {filteredAssets.length > 0 ? (
        <div className="flex-row-el gap-4 overflow-x-auto invisible-scrollbar">
          {filteredAssets.slice(0, 10).map((asset) => (
            <div
              key={asset.id}
              className="flex-col-el shrink-0 w-[160px] cursor-pointer group"
              onClick={() => {
                btnDelay(() => onSelectAsset(asset));
              }}
            >
              <div className="w-[128px] h-[128px] overflow-hidden">
                <img
                  src={asset.url}
                  alt={asset.name}
                  className="w-full h-full object-contain bg-white"
                />
              </div>
              <div className="mt-2 text-[18px] text-gray2-100 truncate">{asset.name}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex-col-el flex-center shrink-0 h-[186px] text-gray2-100">
          <span className="material-symbols-outlined text-[60px] mb-2">image</span>
          <span className="text-[21px]">{i18n.t("MSG_PARENT_PAGE_PLAYER_ASSETS_EMPTY")}</span>
        </div>
      )}
    </div>
  );
}
